/**
 * Promotion Tier Template Hooks
 *
 * TanStack Query hooks for Promotion Tier Template CRUD.
 * Calls zaviago_backend.api.promotion_tier_actions endpoints.
 *
 * Part of Plan 199 (SaaS Promotion Tiers), Epic 02.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";
import { toast } from "sonner";

// ── Types ────────────────────────────────────────────────────

export interface PromotionTierLevel {
  name?: string;
  min_qty: number;
  max_qty: number | null;
  discount_percentage: number;
  override_rate: number | null;
  perk_names: string[]; // parsed from JSON string
}

export interface PromotionPerk {
  name?: string;
  perk_name: string;
  perk_type: "Free Addon" | "Free Shipping" | "Custom Label";
  linked_addon_field: string;
  description: string;
}

export interface AddonVolumeTier {
  name?: string;
  addon_item: string;
  min_qty: number;
  max_qty: number | null;
  rate: number;
}

export interface PromotionTemplateSummary {
  name: string;
  template_name: string;
  enabled: number;
  applies_to: "Item Code" | "Item Group";
  apply_on_item: string | null;
  apply_on_item_group: string | null;
  qty_counting_mode: string;
  tier_count: number;
  perk_count: number;
}

export interface PromotionTemplate {
  name: string;
  template_name: string;
  enabled: number;
  company: string;
  applies_to: "Item Code" | "Item Group";
  apply_on_item: string | null;
  apply_on_item_group: string | null;
  qty_counting_mode: string;
  tier_levels: PromotionTierLevel[];
  perks: PromotionPerk[];
  addon_volume_tiers: AddonVolumeTier[];
}

export interface ProductTiers {
  template_name: string;
  applies_to: string;
  qty_counting_mode: string;
  tier_levels: PromotionTierLevel[];
  perks: PromotionPerk[];
}

// ── Raw types (backend JSON string for perk_names) ───────────

interface RawTierLevel {
  name?: string;
  min_qty: number;
  max_qty: number | null;
  discount_percentage: number;
  override_rate: number | null;
  perk_names: string; // JSON string from backend
}

function parseTierLevels(raw: RawTierLevel[]): PromotionTierLevel[] {
  return (raw || []).map((t) => ({
    ...t,
    perk_names: parsePerkNames(t.perk_names),
  }));
}

function parsePerkNames(raw: string | string[] | null): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function parseTemplate(raw: any): PromotionTemplate {
  return {
    ...raw,
    tier_levels: parseTierLevels(raw.tier_levels),
  };
}

// ── Query Keys ───────────────────────────────────────────────

export const promotionTierKeys = {
  all: () => ["promotion-templates"] as const,
  list: () => ["promotion-templates", "list"] as const,
  detail: (name: string) => ["promotion-templates", "detail", name] as const,
  productTiers: (itemCode: string) =>
    ["promotion-templates", "product-tiers", itemCode] as const,
};

// ── Hooks ────────────────────────────────────────────────────

const API_BASE = "zaviago_backend.api.promotion_tier_actions";

/** Fetch all promotion templates for the current tenant. */
export function usePromotionTemplates() {
  return useQuery<PromotionTemplateSummary[]>({
    queryKey: promotionTierKeys.list(),
    queryFn: () =>
      frappeMethod<PromotionTemplateSummary[]>(
        `${API_BASE}.get_promotion_templates`
      ),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
}

/** Fetch a single promotion template with all child tables. */
export function usePromotionTemplate(name: string) {
  return useQuery<PromotionTemplate>({
    queryKey: promotionTierKeys.detail(name),
    queryFn: async () => {
      const raw = await frappeMethod<any>(
        `${API_BASE}.get_promotion_template`,
        { name }
      );
      return parseTemplate(raw);
    },
    enabled: !!name && name !== "new",
    staleTime: 60 * 1000,
  });
}

/** Save (create or update) a promotion template. */
export function useSavePromotionTemplate() {
  const queryClient = useQueryClient();

  return useMutation<PromotionTemplate, Error, Record<string, any>>({
    mutationFn: async (data) => {
      // Stringify perk_names arrays in tier_levels before sending
      const prepared = {
        ...data,
        tier_levels: (data.tier_levels || []).map((t: any) => ({
          ...t,
          perk_names: JSON.stringify(t.perk_names || []),
          override_rate: t.override_rate ?? null,
        })),
      };
      const raw = await frappeMethod<any>(
        `${API_BASE}.save_promotion_template`,
        { data: JSON.stringify(prepared) }
      );
      return parseTemplate(raw);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({
        queryKey: promotionTierKeys.list(),
      });
      queryClient.invalidateQueries({
        queryKey: promotionTierKeys.detail(saved.name),
      });
      toast.success("Promotion template saved");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to save template");
    },
  });
}

/** Delete a promotion template. */
export function useDeletePromotionTemplate() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; message: string }, Error, string>({
    mutationFn: (name) =>
      frappeMethod<{ success: boolean; message: string }>(
        `${API_BASE}.delete_promotion_template`,
        { name }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: promotionTierKeys.list(),
      });
      toast.success("Template deleted");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete template");
    },
  });
}

/** Fetch tier data for a specific product (used on PDP / PricingCard). */
export function useProductTiers(itemCode: string) {
  return useQuery<ProductTiers | null>({
    queryKey: promotionTierKeys.productTiers(itemCode),
    queryFn: async () => {
      const raw = await frappeMethod<any | null>(
        `${API_BASE}.get_product_tiers`,
        { item_code: itemCode }
      );
      if (!raw) return null;
      return {
        ...raw,
        tier_levels: parseTierLevels(raw.tier_levels),
      };
    },
    enabled: !!itemCode && itemCode !== "new",
    staleTime: 5 * 60 * 1000,
  });
}
